import { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import Notifications from './Notifications'
import SearchAndFilter from './SearchAndFilter'
import CouponCard from './CouponCard'
import { Text } from 'ui'
import { PersonIcon, HeartIcon, CouponIcon, ShareIcon } from './icons'
import FilterModal from './FilterModal'
import ShareModal from './ShareModal'
import Toast from './Toast'
import CouponSlug from './CouponSlug'
import AcceptCouponModal from './AcceptCouponModal'
import IssuedCouponView from './IssuedCouponView'
import { Backdrop, hideScrollbarStyles } from './SharedStyledComponents'
import ConfirmModal from './ConfirmModal'
import PersonalInformation from './PersonalInformation'
import { useCoupons } from '../../../store/CouponsContext'
import { useNotifications } from 'store/NotificationsContext'
import BottomNavMobile, { PublishBottomBarConfig } from './BottomNavMobile'
import PublishCouponModal from './PublishCouponModal'
import AdBanner, { useServedAds, withCardAds } from './AdBanner'
import AdCouponCard from './AdCouponCard'
import { collection, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore'
import { db, Timestamp } from '../../../firebase'
import Cookies from 'js-cookie'
import { generateGUID, getIconTypeFromTitle } from './function'
import { useCouponsAppData } from 'store/CouponsAppDataContext'

const Container = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  background: #FAFAFA;

  @media (max-width: 1081px) {
    height: auto;
    min-height: 100%;
    position: relative;
    &::before {
      content: '';
      position: fixed;
      inset: 0;
      background: #FAFAFA;
      z-index: -1;
      pointer-events: none;
    }
  }
`

const MainContent = styled.div`
  display: flex;
  flex: 1;
  @media (max-width: 860px) {
    flex-direction: column;
    width: 100%;
  }
`

const ContentArea = styled.div<{ alignItems?: string, background?: string, gap?: string }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${props => props.gap};
    position: relative;
  align-items: ${props => props.alignItems || ''};
  background: ${props => props.background || ''};
  @media (max-width: 1081px) {
    padding-bottom:60px;
  }
`

const CouponCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  margin-right: 20px;
  overflow-y: auto;
  max-height: 100%;
  padding-bottom: 120px;
  ${hideScrollbarStyles}
  @media (max-width: 1081px) {
    margin-right: 0;
    height: auto;
    max-height: none;
    overflow: visible;
  }
`

const CouponCardsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 22px;
  width: 100%;
  @media (max-width: 1081px) {
    justify-content: center;
  }
`
const FloatingButtons = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);   
  display: inline-flex;
  padding: 4px;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 4px;
  border-radius: 16px;
  background: var(--Neutral-Surface-Minimal, #FFF);
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.25);
  z-index: 999;
  @media (max-width: 1081px) {
    display: none;
  }
`;

const FloatingButton = styled.button<{ color: string }>`
  display: flex;
  padding: 16px 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background: ${props => props.color};
  border-radius: 100px;
  border: none;
  outline: none;
  cursor: pointer;
  border-radius: 12px;

  &:disabled {
    background: #D1D1D1;
    cursor: not-allowed;
    pointer-events: none;
  }
`


const JustASquare = styled.div`
  width: 97px;
  height: 97px;
  flex-shrink: 0;
  border-radius: 16px;
  border: 1px dashed var(--Neutral-Text-Disable, #888);
`

const SearchWrapper = styled.div<{ hideOnMobile?: boolean }>`
  @media (max-width: 1081px) {
    display: ${props => props.hideOnMobile ? 'none' : 'block'};
  }
`;


// ---- Per-type filter state, persisted in localStorage ----------------------------------------
// General and local coupons each have their own independent filter state. Switching tabs
// preserves whatever the user had set on the other side, and the values survive a refresh.

type ValidityFiltersState = { lastChance: boolean; lastWeek: boolean; longExpiry: boolean }

type FiltersState = {
    selectedCategories: string[]
    usedAdvancedFilters: boolean
    selectedAdvancedFiltersCategories: string[]
    validityFilters: ValidityFiltersState
    priceRange: { min: number; max: number }
    sortOption: any
    selectedTags: string[]
}

type FiltersByType = { general: FiltersState; local: FiltersState }

const DEFAULT_FILTERS: FiltersState = {
    selectedCategories: [],
    usedAdvancedFilters: false,
    selectedAdvancedFiltersCategories: [],
    validityFilters: { lastChance: false, lastWeek: false, longExpiry: false },
    priceRange: { min: 20, max: 2000 },
    sortOption: 0,
    selectedTags: [],
}

const FILTERS_STORAGE_KEY = 'mekomi.couponFilters.v1'

type StoredFilters = { activeType: 'general' | 'local'; byType: FiltersByType }

const defaultStoredFilters = (): StoredFilters => ({
    activeType: 'general',
    byType: { general: { ...DEFAULT_FILTERS }, local: { ...DEFAULT_FILTERS } },
})

const readFiltersStorage = (): StoredFilters => {
    if (typeof window === 'undefined') return defaultStoredFilters()
    try {
        const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY)
        if (!raw) return defaultStoredFilters()
        const parsed = JSON.parse(raw) as Partial<StoredFilters>
        const activeType = parsed.activeType === 'local' ? 'local' : 'general'
        return {
            activeType,
            byType: {
                general: { ...DEFAULT_FILTERS, ...(parsed.byType?.general ?? {}) },
                local: { ...DEFAULT_FILTERS, ...(parsed.byType?.local ?? {}) },
            },
        }
    } catch {
        return defaultStoredFilters()
    }
}

const writeFiltersStorage = (data: StoredFilters) => {
    if (typeof window === 'undefined') return
    try { window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(data)) } catch { /* quota / private mode */ }
}


const Coupons: FC = () => {
    // Get data from context
    const appData = useCouponsAppData()
    const { coupons, categories, categoriesByScope, isLoading, isLiked, toggleLike, customerPreferences, saveCustomerPreferences, refreshLocalCoupons, trackView, trackClick, trackShare, getFirestoreId, issuedCodes, requestCouponCode } = useCoupons()
    const { filterBlockedUsers, filterByCustomerPreferences } = useNotifications()
    const { ads: cardAds, trackClick: trackCardAdClick } = useServedAds('coupon_card')

    // Filters: per-type state (general/local) loaded from and persisted to localStorage. The
    // initial filterType also comes from storage so the user lands back on whatever tab they
    // last used.
    const initialStoredFilters = useMemo(() => readFiltersStorage(), [])
    const [filterType, setFilterType] = useState<'general' | 'local'>(initialStoredFilters.activeType)
    const [filtersByType, setFiltersByType] = useState<FiltersByType>(initialStoredFilters.byType)
    const [floatingFilter, setFloatingFilter] = useState<'benefits' | 'liked' | 'personal' | 'notifications'>('benefits')
    const [searchValue, setSearchValue] = useState<string>('')
    const [searchQuery, setSearchQuery] = useState<string>('')

    // Persist filter state on every change.
    useEffect(() => {
        writeFiltersStorage({ activeType: filterType, byType: filtersByType })
    }, [filterType, filtersByType])

    // Active filters for the current tab — destructured so existing read-sites keep working.
    const activeFilters = filtersByType[filterType]
    const { selectedCategories, usedAdvancedFilters, selectedAdvancedFiltersCategories, validityFilters, priceRange, sortOption, selectedTags } = activeFilters

    // Wrapper setters — they keep the per-field API the rest of the component already uses, but
    // route updates into the active tab's slice of the combined state.
    type SetState<T> = T | ((prev: T) => T)
    const updateActiveFilters = useCallback(<K extends keyof FiltersState>(key: K, value: SetState<FiltersState[K]>) => {
        setFiltersByType(prev => {
            const current = prev[filterType][key]
            const next = (typeof value === 'function' ? (value as (p: FiltersState[K]) => FiltersState[K])(current) : value)
            if (Object.is(next, current)) return prev
            return { ...prev, [filterType]: { ...prev[filterType], [key]: next } }
        })
    }, [filterType])

    const setSelectedCategories = useCallback((v: SetState<string[]>) => updateActiveFilters('selectedCategories', v), [updateActiveFilters])
    const setUsedAdvancedFilters = useCallback((v: SetState<boolean>) => updateActiveFilters('usedAdvancedFilters', v), [updateActiveFilters])
    const setSelectedAdvancedFiltersCategories = useCallback((v: SetState<string[]>) => updateActiveFilters('selectedAdvancedFiltersCategories', v), [updateActiveFilters])
    const setValidityFilters = useCallback((v: SetState<ValidityFiltersState>) => updateActiveFilters('validityFilters', v), [updateActiveFilters])
    const setPriceRange = useCallback((v: SetState<{ min: number; max: number }>) => updateActiveFilters('priceRange', v), [updateActiveFilters])
    const setSortOption = useCallback((v: SetState<any>) => updateActiveFilters('sortOption', v), [updateActiveFilters])
    const setSelectedTags = useCallback((v: SetState<string[]>) => updateActiveFilters('selectedTags', v), [updateActiveFilters])

    // Price slider scale: starts at 20. Upper bound is dynamic — at least 2000, expands to cover
    // the priciest coupon. Default range covers everything so no products are silently filtered
    // out.
    const sliderMax = useMemo(() => {
        const prices = coupons.map(c => parseFloat(c.price)).filter(p => !isNaN(p))
        return Math.max(2000, ...prices)
    }, [coupons])
    // When coupons load and the user hasn't applied advanced filters yet, keep the default max
    // pinned to the slider's full range.
    useEffect(() => {
        if (!usedAdvancedFilters) {
            setPriceRange(prev => prev.max === sliderMax ? prev : { ...prev, max: sliderMax })
        }
    }, [sliderMax, usedAdvancedFilters, setPriceRange])

    // Sharing state
    const [isShareButtonActive, setIsShareButtonActive] = useState(false)
    const [checkedCoupons, setCheckedCoupons] = useState<Set<number>>(new Set())
    const [selectedContacts, setSelectedContacts] = useState<Set<number>>(new Set())
    const [shareSearchQuery, setShareSearchQuery] = useState('')
    const [isSharing, setIsSharing] = useState(false)

    // Filter modal state
    const [filteredCouponsCount, setFilteredCouponsCount] = useState(0)
    const [applyFiltersFromModal, setApplyFiltersFromModal] = useState<(() => void) | null>(null)

    // Modals state
    const [modals, setModals] = useState({
        isFilterModalOpen: false,
        isShareModalOpen: false,
        isGetCouponModalOpen: false,
        isIssuedCouponViewOpen: false,
        isConfirmModalOpen: false,
        isPublishModalOpen: false,
    })

    // Issued coupon code state
    const [currentIssuedCode, setCurrentIssuedCode] = useState<import('./types').IssuedCode | null>(null)
    const [isIssuingCode, setIsIssuingCode] = useState(false)

    // Publish modal bottom bar config (for BottomNavMobile on mobile)
    const [publishBottomBarConfig, setPublishBottomBarConfig] = useState<PublishBottomBarConfig | null>(null)

    // Coupon view state
    const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null)

    // Accept coupon modal state
    const [isCodeCopied, setIsCodeCopied] = useState(false)

    // Toast state
    const [toast, setToast] = useState({
        isVisible: false,
        title: 'ההטבות שותפו בהצלחה!',
        subtitle: 'בחרת לשתף ${checkedCoupons.size} הטבות'
    })

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({
        title: '',
        subtitle: '',
        confirmButtonText: '',
        cancelButtonText: '',
        isConfettiVisible: false,
        isGiftIconVisible: false,
        confirmButtonBackground: '#007EFF',
        confirmButtonBorderColor: '#007EFF',
        confirmButtonTextColor: 'white',
        cancelButtonBackground: '#FFF',
        cancelButtonBorderColor: '#B0B0B0',
        cancelButtonTextColor: '#272727',
    })

    // State for managing member blocking
    const [blockedMembersCount, setBlockedMembersCount] = useState(0)
    const [confirmModalSource, setConfirmModalSource] = useState<'notifications' | 'blockMembers' | null>(null)

    // Hide the bottom categories chip row once the user scrolls the coupon list — desktop only
    // (FiltersLine is already hidden via media query under 1081px).
    const couponCardsRef = useRef<HTMLDivElement>(null)
    const [isCouponListScrolled, setIsCouponListScrolled] = useState(false)
    useEffect(() => {
        const el = couponCardsRef.current
        if (!el) return
        const onScroll = () => setIsCouponListScrolled(el.scrollTop > 10)
        el.addEventListener('scroll', onScroll, { passive: true })
        return () => el.removeEventListener('scroll', onScroll)
    }, [floatingFilter, selectedCouponId])

    // Handle URL changes and initial load
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const couponId = params.get('couponId')
        if (couponId) {
            setSelectedCouponId(Number(couponId))
        }
    }, [])

    // Handle PayPlus payment callback (success/failure)
    useEffect(() => {
        const handlePayPlusCallback = async () => {
            const params = new URLSearchParams(window.location.search);
            const isSuccess = params.get('payplusSuccess') === 'true';
            const isFailure = params.get('payplusFailure') === 'true';
            const orderId = params.get('orderId');

            if (!orderId || (!isSuccess && !isFailure)) return;

            // Clean URL params immediately to prevent re-processing on refresh
            const url = new URL(window.location.href);
            url.searchParams.delete('payplusSuccess');
            url.searchParams.delete('payplusFailure');
            url.searchParams.delete('orderId');
            window.history.replaceState({}, '', url.toString());

            try {
                // Fetch the transaction record to get the couponId
                const transactionRef = doc(db, 'coupon_transactions', orderId);
                const transactionSnap = await getDoc(transactionRef);

                if (!transactionSnap.exists()) {
                    console.error('Transaction not found:', orderId);
                    setToast({ isVisible: true, title: 'שגיאה', subtitle: 'לא נמצאה עסקה מתאימה' });
                    setTimeout(() => setToast(prev => ({ ...prev, isVisible: false })), 3000);
                    return;
                }

                const transactionData = transactionSnap.data();
                const couponId = transactionData.couponId;

                if (isSuccess) {
                    // Payment cleared, so the coupon leaves 'pending_payment' and enters the
                    // admin approval queue ('open' tab in the admin panel matches pending /
                    // inReview). It used to jump straight to 'approved', which published it
                    // live without any review.
                    const couponRef = doc(db, 'coupons', couponId);
                    await updateDoc(couponRef, { status: 'pending' });

                    // Update transaction status
                    await updateDoc(transactionRef, {
                        status: 'completed',
                        completedAt: Timestamp.now(),
                        updatedAt: Timestamp.now(),
                    });

                    refreshLocalCoupons();

                    setToast({ isVisible: true, title: 'התשלום בוצע בהצלחה!', subtitle: 'ההטבה נשלחה לאישור ותפורסם לאחר אישור מנהל' });
                    setTimeout(() => setToast(prev => ({ ...prev, isVisible: false })), 5000);

                } else if (isFailure) {
                    // Mark coupon as payment_failed
                    const couponRef = doc(db, 'coupons', couponId);
                    await updateDoc(couponRef, { status: 'payment_failed' });

                    // Update transaction status
                    await updateDoc(transactionRef, {
                        status: 'failed',
                        failedAt: Timestamp.now(),
                        updatedAt: Timestamp.now(),
                    });

                    setToast({ isVisible: true, title: 'התשלום נכשל', subtitle: 'לא בוצע חיוב. ניתן לנסות שנית' });
                    setTimeout(() => setToast(prev => ({ ...prev, isVisible: false })), 5000);
                }
            } catch (error) {
                console.error('Error processing PayPlus callback:', error);
                setToast({ isVisible: true, title: 'שגיאה', subtitle: 'אירעה שגיאה בעיבוד התשלום' });
                setTimeout(() => setToast(prev => ({ ...prev, isVisible: false })), 3000);
            }
        };

        handlePayPlusCallback();
    }, []);

    const handleCouponClick = (couponId: number) => {
        setSelectedCouponId(couponId)
        trackView(couponId)
        // Reset sharing state when viewing a coupon
        setCheckedCoupons(new Set())
        setIsShareButtonActive(false)
        const url = new URL(window.location.href)
        url.searchParams.set('couponId', couponId.toString())
        window.history.pushState({}, '', url.toString())
    }

    const handleCouponSelectFromSearch = (couponId: number) => {
        handleCouponClick(couponId)
    }

    const handleBackToList = () => {
        setSelectedCouponId(null)
        const url = new URL(window.location.href)
        url.searchParams.delete('couponId')
        window.history.pushState({}, '', url.toString())
    }

    // Clear both the visible search input and the applied search query. Used whenever the user
    // engages with the advanced-filter flow (open modal, apply, clear, or remove a chip) — search
    // and advanced filters are intentionally separate workflows.
    const clearSearch = useCallback(() => {
        setSearchValue('')
        setSearchQuery('')
    }, [])

    const handleAdvancedFiltersCategoriesChange = (categories: string[]) => {
        setSelectedAdvancedFiltersCategories(categories)
        clearSearch()
    }
    const handleValidityFilterChange = (filterName: 'lastChance' | 'lastWeek' | 'longExpiry') => {
        setValidityFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName],
        }))
    }

    const handleReset = () => {
        setSelectedAdvancedFiltersCategories([])
        setValidityFilters({
            lastChance: false,
            lastWeek: false,
            longExpiry: false,
        })
        setPriceRange({ min: 20, max: sliderMax })
        setSortOption(0) // Reset to Newest
        setSelectedTags([])
    }

    const handleApplyFilters = (selectedSortOption: any, minPrice: number, maxPrice: number) => {
        setUsedAdvancedFilters(true)
        setPriceRange({ min: minPrice, max: maxPrice })
        setSortOption(selectedSortOption)
        clearSearch()
    }

    const handleClearAdvancedFilters = () => {
        setUsedAdvancedFilters(false)
        handleReset()
        clearSearch()
    }

    // Open the filter modal and clear any search the user had typed — search & advanced filters
    // shouldn't compose.
    const openFilterModal = useCallback((open: boolean) => {
        if (open) clearSearch()
        setModals(prev => ({ ...prev, isFilterModalOpen: open }))
    }, [clearSearch])

    const handleShareCoupons = (sharedCount: number) => {
        setToast({
            isVisible: true,
            title: 'ההטבות שותפו בהצלחה!',
            subtitle: `בחרת לשתף ${sharedCount} הטבות`
        })
        setIsShareButtonActive(false)
        setCheckedCoupons(new Set())
        setModals(prev => ({ ...prev, isShareModalOpen: false }))

        setTimeout(() => {
            setToast(prev => ({ ...prev, isVisible: false }))
        }, 3000)
    }

    const handleWhatsAppShare = () => {
        if (checkedCoupons.size === 0) return

        // Get all selected coupon IDs and construct links
        const selectedCouponIds = Array.from(checkedCoupons)
        const links = selectedCouponIds.map(couponId =>
            `https://mekome.net/home/coupons?couponId=${couponId}`
        )

        // Construct the message with Hebrew text - use plural if multiple coupons
        const isMultiple = selectedCouponIds.length > 1
        const messageText = isMultiple
            ? 'שלום רב, מצאתי הטבות במקומי שאני חושב/ת שתעניינה אותך !'
            : 'שלום רב, מצאתי הטבה במקומי שאני חושב/ת שתעניין אותך !'

        // Combine message text with all links (one per line)
        const message = `${messageText}\n${links.join('\n')}`

        // Encode the message for URL
        const encodedMessage = encodeURIComponent(message)
        const whatsappUrl = `https://wa.me/?text=${encodedMessage}`

        // Open WhatsApp in a new tab
        window.open(whatsappUrl, '_blank')
        trackShare(Array.from(checkedCoupons))

        // Clear checked items after sharing
        setCheckedCoupons(new Set())
        setIsShareButtonActive(false)
    }

    // Helper function to check if string is a phone number
    const isPhoneNumber = (str: string): boolean => {
        // Check if string contains only digits, spaces, dashes, and plus sign
        // and has at least 9 digits (minimum for Israeli phone numbers)
        const digitsOnly = str.replace(/\D/g, '')
        return digitsOnly.length >= 9 && digitsOnly.length <= 15
    }

    // Helper function to open WhatsApp with message
    const openWhatsApp = (phoneNumber: string, couponTitle: string, couponCode: string) => {
        // Clean phone number (remove non-digits)
        let cleanPhone = phoneNumber.replace(/\D/g, '')

        // If the phone number doesn't start with country code, add Israeli country code
        if (!cleanPhone.startsWith('972') && !cleanPhone.startsWith('+972')) {
            // Remove leading zero if exists
            if (cleanPhone.startsWith('0')) {
                cleanPhone = cleanPhone.substring(1)
            }
            // Add Israeli country code
            cleanPhone = '972' + cleanPhone
        }

        // Create WhatsApp message in Hebrew
        // A benefit with no redemption code (WhatsApp benefits) opens the chat with just
        // the request line — there is nothing to quote.
        const codeLine = couponCode ? `\nקוד קופון: ${couponCode}` : ''
        const message = `שלום, אני מעוניין/ת בהטבה: ${couponTitle}${codeLine}`
        const encodedMessage = encodeURIComponent(message)

        // WhatsApp URL for web
        const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`

        // Open WhatsApp in new tab
        window.open(whatsappUrl, '_blank')
    }

    // Handler for issuing a unique coupon code via Firebase callable
    const handleGetCoupon = useCallback(async () => {
        if (!selectedCouponId) return

        // WhatsApp benefits are redeemed in the conversation itself, so there is no code to
        // issue: hand the user straight to the chat instead of the accept-code modal. A
        // static code from a benefit published before this split is still quoted if present.
        const coupon = coupons.find(c => c.id === selectedCouponId)
        if (coupon?.ctaType === 'whatsapp' && coupon.ctaValue) {
            trackClick(selectedCouponId)
            openWhatsApp(coupon.ctaValue, coupon.title, coupon.couponCode || '')
            return
        }

        const firestoreId = getFirestoreId(selectedCouponId)
        if (!firestoreId) {
            // Fallback: no Firestore ID available — open old static modal
            setModals(prev => ({ ...prev, isGetCouponModalOpen: true }))
            return
        }
        const couponTitle = coupon?.title ?? ''
        setIsIssuingCode(true)
        try {
            const issued = await requestCouponCode(firestoreId, couponTitle)
            setCurrentIssuedCode(issued)
            setModals(prev => ({ ...prev, isGetCouponModalOpen: true }))
        } catch (err) {
            console.error('Failed to issue coupon code:', err)
            // Fallback: show static AcceptCouponModal
            setModals(prev => ({ ...prev, isGetCouponModalOpen: true }))
        } finally {
            setIsIssuingCode(false)
        }
    }, [selectedCouponId, coupons, getFirestoreId, requestCouponCode, trackClick])

    // The publisher's site opens in a new tab rather than an embedded frame: a framed store
    // cannot keep a cart across visits (third-party storage is partitioned or blocked), which
    // breaks the purchase the benefit is meant to lead to.
    const openPublisherSite = (url: string) => {
        const target = url.trim()
        // A bare domain would otherwise resolve against our own origin.
        const absolute = /^https?:\/\//i.test(target) ? target : `https://${target}`
        window.open(absolute, '_blank', 'noopener')
    }

    // Handler for opening product link (either URL or WhatsApp)
    const handleOpenProductLink = () => {
        if (!selectedCouponId) return

        trackClick(selectedCouponId)

        const coupon = coupons.find(c => c.id === selectedCouponId)
        if (!coupon) return

        // Use the dynamically issued code if available, otherwise fall back to static couponCode
        const codeForWhatsApp = currentIssuedCode?.code || coupon.couponCode || ''

        // New schema: ctaType + ctaValue
        if (coupon.ctaType === 'whatsapp' && coupon.ctaValue) {
            openWhatsApp(coupon.ctaValue, coupon.title, codeForWhatsApp)
            setModals(prev => ({ ...prev, isGetCouponModalOpen: false, isIssuedCouponViewOpen: false }))
        } else if (coupon.ctaType === 'url' && coupon.ctaValue) {
            openPublisherSite(coupon.ctaValue)
            setModals(prev => ({ ...prev, isGetCouponModalOpen: false, isIssuedCouponViewOpen: false }))
        } else if (coupon.productLink) {
            // Fallback for old schema
            const productLink = coupon.productLink.trim()
            if (isPhoneNumber(productLink)) {
                openWhatsApp(productLink, coupon.title, codeForWhatsApp)
                setModals(prev => ({ ...prev, isGetCouponModalOpen: false, isIssuedCouponViewOpen: false }))
            } else {
                openPublisherSite(productLink)
                setModals(prev => ({ ...prev, isGetCouponModalOpen: false, isIssuedCouponViewOpen: false }))
            }
        }
    }


    const handleContactSelect = (userId: number) => {
        setSelectedContacts(prev => {
            const newSet = new Set(prev)
            if (newSet.has(userId)) {
                newSet.delete(userId)
            } else {
                newSet.add(userId)
            }
            return newSet
        })
    }

    // Save notification to Firestore (without sending push notification)
    const saveNotificationToFirestore = async (
        userId: number,
        title: string,
        description: string,
        couponId: string,
        communityId: number,
        messageId: string
    ): Promise<void> => {
        try {
            console.log(`💾 Saving notification to Firestore for user ${userId} (no push)`)

            const notificationsRef = collection(db, 'notifications')
            const notificationData: any = {
                userId,
                title,
                description,
                iconType: getIconTypeFromTitle(title),
                read: false,
                createdAt: Timestamp.now(),
                messageId,
                couponId,
                communityId,
            }

            const newNotificationRef = doc(notificationsRef)
            await setDoc(newNotificationRef, notificationData)
            console.log(`✅ Notification saved to Firestore for user ${userId}`)
        } catch (error) {
            console.error(`Error saving notification to Firestore for user ${userId}:`, error)
        }
    }

    // Send notification for a single coupon to a list of users using web API
    const sendCouponNotification = async (
        couponId: string,
        couponTitle: string,
        userIds: number[],
        senderName: string
    ): Promise<{ success: boolean; messageId?: number }> => {
        try {
            if (!appData.userId) {
                console.error('User not logged in')
                return { success: false }
            }

            const communityId = appData.currentCommunityId
            if (!communityId) {
                console.error('No community selected')
                return { success: false }
            }

            const apiUrl = `${process.env.REACT_APP_API_MEKOMI_BASE_URL}/sapi/CouponMessages/${communityId}`
            const apiToken = process.env.REACT_APP_X_ZUMO_APPLICATION
            const authToken = Cookies.get('mekomeRefresh')
            const jwtToken = appData.jwtToken

            const requestBody = {
                Guid: generateGUID(),
                userList: userIds,
                couponId: couponId,
                title: `${senderName} שיתפ/ה איתך הטבה`,
                body: couponTitle,
            }

            console.log('📤 Sending notification:', requestBody)

            const headers: any = {
                'Content-Type': 'application/json',
                'X-ZUMO-APPLICATION': apiToken || '',
            }

            // Add Authorization header if JWT token exists
            if (jwtToken) {
                headers['Authorization'] = `Bearer ${jwtToken}`
            }

            // Add Cookie header if refresh token exists
            if (authToken) {
                headers['Cookie'] = `mekomeRefresh=${authToken}`
            }

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify(requestBody),
            })

            const responseData = await response.json()
            console.log('📥 Notification response:', responseData)

            const success = response.ok && (responseData.status === 'ok' || response.status === 200)
            const messageId = responseData?.messageId

            return { success, messageId }
        } catch (error) {
            console.error('Error sending notification:', error)
            return { success: false }
        }
    }

    const handleShare = async () => {
        const contactCount = selectedContacts.size
        // Convert checkCoupons set to array
        const selectedCouponIds = Array.from(checkedCoupons)

        if (contactCount === 0 || selectedCouponIds.length === 0) {
            // Clear and close happens at the end
            setModals(prev => ({ ...prev, isShareModalOpen: false }))
            return
        }

        setIsSharing(true)

        try {
            // Get user info
            if (!appData.userId) {
                console.error('User not logged in')
                setIsSharing(false)
                return
            }

            const senderUserId = String(appData.userId)
            const senderName = appData.givenName || appData.familyName || appData.displayName || 'משתמש'

            // Get selected user IDs as strings
            const receiverUserIds = Array.from(selectedContacts).map(id => String(id))

            // Get communityId
            const communityId = appData.currentCommunityId
            if (!communityId) {
                console.error('No community selected')
                setIsSharing(false)
                return
            }

            console.log('🔄 Starting notification filtering process...')
            console.log(`📤 Sender: ${senderUserId} (${senderName})`)
            console.log(`👥 Selected receivers: ${receiverUserIds.length} users`)

            // Step 1: Filter out users who have blocked the sender
            const notBlockedUsers = await filterBlockedUsers(senderUserId, receiverUserIds)
            console.log(`✅ Not blocked: ${notBlockedUsers.length} users`)

            // Step 2: From the not-blocked users, separate by push notification preferences
            const usersWithPushEnabled = await filterByCustomerPreferences(notBlockedUsers)
            console.log(`✅ Push enabled: ${usersWithPushEnabled.length} users`)

            // Step 3: Find users who haven't blocked sender but have push disabled
            const usersWithPushDisabled = notBlockedUsers.filter(
                (userId) => !usersWithPushEnabled.includes(userId)
            )
            console.log(`📴 Push disabled (will save to Firestore only): ${usersWithPushDisabled.length} users`)

            // Send notification for each coupon
            for (const couponId of selectedCouponIds) {
                // Find coupon by numeric ID
                const coupon = coupons.find((c) => c.id === couponId)
                if (!coupon) {
                    console.warn(`Coupon ${couponId} not found`)
                    continue
                }

                // Use Firestore ID for the notification
                const firestoreId = coupon.firestoreId
                const couponTitle = coupon.title
                const title = `${senderName} שיתפ/ה איתך הטבה`
                const messageId = generateGUID()

                // Step 4: Send push notification to users with push enabled
                if (usersWithPushEnabled.length > 0) {
                    const pushUserIds = usersWithPushEnabled.map((id) => parseInt(id, 10))
                    console.log(`📲 Sending push notification to ${pushUserIds.length} users...`)

                    const result = await sendCouponNotification(
                        firestoreId,
                        couponTitle,
                        pushUserIds,
                        senderName
                    )

                    if (!result.success) {
                        console.error(`Failed to send notification for coupon ${couponId}`)
                    } else {
                        console.log(`✅ Push notification sent successfully`)
                    }

                    // Step 5a: Save to Firestore for users with push enabled (to ensure notification is stored)
                    console.log(`💾 Saving notifications to Firestore for ${usersWithPushEnabled.length} users with push enabled...`)
                    for (const userId of usersWithPushEnabled) {
                        await saveNotificationToFirestore(
                            parseInt(userId, 10),
                            title,
                            couponTitle,
                            firestoreId,
                            communityId,
                            messageId
                        )
                    }
                }

                // Step 5b: Save to Firestore for users with push disabled (no push notification sent)
                if (usersWithPushDisabled.length > 0) {
                    console.log(`💾 Saving notifications to Firestore for ${usersWithPushDisabled.length} users without push...`)

                    for (const userId of usersWithPushDisabled) {
                        await saveNotificationToFirestore(
                            parseInt(userId, 10),
                            title,
                            couponTitle,
                            firestoreId,
                            communityId,
                            messageId
                        )
                    }
                }
            }

            console.log('✅ All notifications processed successfully')

            // Show toast
            handleShareCoupons(contactCount)
            trackShare(Array.from(checkedCoupons))

        } catch (error) {
            console.error('Error in handleShare:', error)
        } finally {
            setIsSharing(false)
            // Clear selected contacts
            setSelectedContacts(new Set())
            setShareSearchQuery('')
        }
    }

    // Handler for when confirm is clicked from BlockCouponsModal
    const handleConfirmFromBlockMembers = () => {
        // Show toast with blocking success message
        setToast({
            isVisible: true,
            title: 'החברים שבחרת נחסמו בהצלחה!',
            subtitle: `בחרת לחסום ${blockedMembersCount} חברים`
        })

        setTimeout(() => {
            setToast(prev => ({ ...prev, isVisible: false }))
        }, 3000)
    }

    // Get the appropriate onConfirm handler based on source
    const getOnConfirmHandler = () => {
        if (confirmModalSource === 'blockMembers') {
            return handleConfirmFromBlockMembers
        }
        return undefined
    }

    // Use categories from context (already includes 'הכל' at the beginning)
    // categories is already provided from context

    // Memoized callback for registering the apply filters function
    const handleRegisterApplyCallback = useCallback((callback: () => void) => {
        setApplyFiltersFromModal(() => callback)
    }, [])

    const handleCheckboxChange = (couponId: number, isChecked: boolean) => {
        setCheckedCoupons(prev => {
            const newSet = new Set(prev)
            if (isChecked) {
                newSet.add(couponId)
            } else {
                newSet.delete(couponId)
            }
            return newSet
        })
        setIsShareButtonActive(isChecked)
    }

    // Handler for single category selection (used in SearchAndFilter)
    const handleSingleCategoryChange = (category: string) => {
        // Toggle category
        if (selectedCategories.includes(category)) {
            setSelectedCategories([])
        } else {
            setSelectedCategories([category])
        }
    }

    const availableTags = useMemo(() => {
        const tagSet = new Set<string>()
        coupons.forEach(c => (c.tags ?? []).forEach(t => tagSet.add(t)))
        return Array.from(tagSet).sort()
    }, [coupons])

    // Categories visible in the current view depend on the filterType (general → global-scoped,
    // local → local-scoped). Strip "הכל" since the consumers add their own "all" chip / handling.
    const activeCategories = useMemo(
        () => (filterType === 'general' ? categoriesByScope.global : categoriesByScope.local).filter(c => c !== 'הכל'),
        [filterType, categoriesByScope]
    )
    // Step1 of PublishCouponModal can only create local coupons, so it always sees the local list.
    const localCategoriesForPublish = useMemo(
        () => categoriesByScope.local.filter(c => c !== 'הכל'),
        [categoriesByScope]
    )

    const filteredCoupons = coupons.filter(coupon => {
        // If viewing liked coupons, ignore all other filters (general/local, categories, search, etc.)
        if (floatingFilter === 'liked') {
            return isLiked(coupon.id)
        }

        // Always apply general/local filter
        const matchesType = coupon.type === filterType
        const matchesFloatingFilter = floatingFilter === 'benefits' || floatingFilter === 'personal'

        // Category filter. Empty array or "הכל" both mean "no category filter" — must match the
        // FilterModal's count logic so the count and the rendered list never disagree.
        let matchesCategory = true
        if (usedAdvancedFilters) {
            const isAllCategories = selectedAdvancedFiltersCategories.length === 0 || selectedAdvancedFiltersCategories.includes('הכל')
            matchesCategory = isAllCategories || selectedAdvancedFiltersCategories.includes(coupon.category)
        } else {
            matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(coupon.category)
        }

        // Search filter
        const matchesSearch = searchQuery.length === 0 || coupon.title.toLowerCase().includes(searchQuery.toLowerCase())

        // Price filter (only if advanced filters are used)
        let matchesPrice = true
        if (usedAdvancedFilters) {
            const couponPrice = parseFloat(coupon.price)
            matchesPrice = couponPrice >= priceRange.min && couponPrice <= priceRange.max
        }

        // Validity filter (only if advanced filters are used)
        let matchesValidity = true
        if (usedAdvancedFilters && (validityFilters.lastChance || validityFilters.lastWeek || validityFilters.longExpiry)) {
            const now = new Date()
            const endAt = coupon.endAt ? new Date(coupon.endAt) : null

            if (!endAt) {
                matchesValidity = false
            } else {
                const hoursLeft = (endAt.getTime() - now.getTime()) / (1000 * 60 * 60)
                const daysLeft = hoursLeft / 24

                matchesValidity = false
                // הזדמנות אחרונה - 24 hours or less left
                if (validityFilters.lastChance && hoursLeft > 0 && hoursLeft <= 24) matchesValidity = true
                // שבוע אחרון - less than 7 days (but more than 24 hours)
                if (validityFilters.lastWeek && hoursLeft > 24 && daysLeft <= 7) matchesValidity = true
                // חודש ויותר - more than 30 days left
                if (validityFilters.longExpiry && daysLeft > 30) matchesValidity = true
            }
        }

        const matchesTags = selectedTags.length === 0 ||
            (coupon.tags ?? []).some(tag => selectedTags.includes(tag))

        return matchesType && matchesFloatingFilter && matchesCategory && matchesSearch && matchesPrice && matchesValidity && matchesTags
    }).sort((a, b) => {
        // Ranked coupons first, ascending rank
        const aRank = a.priorityRank ?? Infinity
        const bRank = b.priorityRank ?? Infinity
        if (aRank !== bRank) return aRank - bRank

        // Apply price sorting if advanced filters are used with price sort option
        if (usedAdvancedFilters && sortOption !== 0) {
            if (sortOption === 1) { // Price High to Low
                return parseFloat(b.price) - parseFloat(a.price)
            } else if (sortOption === 2) { // Price Low to High
                return parseFloat(a.price) - parseFloat(b.price)
            }
        }

        // Default sorting: by time remaining (least time first, expired at end)
        const now = new Date()
        const aEndAt = a.endAt ? new Date(a.endAt) : null
        const bEndAt = b.endAt ? new Date(b.endAt) : null

        // If no endAt, put at the end
        if (!aEndAt && !bEndAt) return 0
        if (!aEndAt) return 1
        if (!bEndAt) return -1

        const aExpired = aEndAt <= now
        const bExpired = bEndAt <= now

        // Active coupons come before expired ones
        if (!aExpired && bExpired) return -1
        if (aExpired && !bExpired) return 1

        // Both active: sort by least time remaining first (ascending endAt)
        // Both expired: sort by most recently expired first (descending endAt)
        if (!aExpired && !bExpired) {
            return aEndAt.getTime() - bEndAt.getTime()
        } else {
            return bEndAt.getTime() - aEndAt.getTime()
        }
    })

    const selectedCoupon = selectedCouponId ? coupons.find(c => c.id === selectedCouponId) : undefined
    const isSelectedCouponExpired = !!(selectedCoupon?.endAt && new Date(selectedCoupon.endAt).getTime() <= Date.now())

    return (
        <Container>
            <MainContent>
                <Notifications
                    setIsConfirmModalOpen={(open) => setModals(prev => ({ ...prev, isConfirmModalOpen: open }))}
                    setConfirmModalTitle={(title) => setConfirmModal(prev => ({ ...prev, title }))}
                    setConfirmModalSubtitle={(subtitle) => setConfirmModal(prev => ({ ...prev, subtitle }))}
                    setConfirmModalConfirmButtonText={(text) => setConfirmModal(prev => ({ ...prev, confirmButtonText: text }))}
                    setConfirmModalCancelButtonText={(text) => setConfirmModal(prev => ({ ...prev, cancelButtonText: text }))}
                    setConfirmModalIsConfettiVisible={(visible) => setConfirmModal(prev => ({ ...prev, isConfettiVisible: visible }))}
                    setConfirmModalIsGiftIconVisible={(visible) => setConfirmModal(prev => ({ ...prev, isGiftIconVisible: visible }))}
                    setConfirmModalConfirmButtonBackground={(bg) => setConfirmModal(prev => ({ ...prev, confirmButtonBackground: bg }))}
                    setConfirmModalConfirmButtonBorderColor={(color) => setConfirmModal(prev => ({ ...prev, confirmButtonBorderColor: color }))}
                    setConfirmModalConfirmButtonTextColor={(color) => setConfirmModal(prev => ({ ...prev, confirmButtonTextColor: color }))}
                    setConfirmModalCancelButtonBackground={(bg) => setConfirmModal(prev => ({ ...prev, cancelButtonBackground: bg }))}
                    setConfirmModalCancelButtonBorderColor={(color) => setConfirmModal(prev => ({ ...prev, cancelButtonBorderColor: color }))}
                    setConfirmModalCancelButtonTextColor={(color) => setConfirmModal(prev => ({ ...prev, cancelButtonTextColor: color }))}
                    setConfirmModalSource={setConfirmModalSource}
                    isMobileView={floatingFilter === 'notifications'}
                    onCouponClick={handleCouponClick}
                />
                <ContentArea
                    gap={selectedCouponId ? '10px' : '24px'}
                    alignItems={floatingFilter === 'personal' ? 'center' : ''}
                    background={floatingFilter === 'personal' || floatingFilter === 'notifications' ? '#F6F6F6' : ''}
                >
                    <SearchWrapper hideOnMobile={!!selectedCouponId || floatingFilter === 'notifications'}>
                        <SearchAndFilter
                            filterType={filterType}
                            onFilterChange={setFilterType}
                            coupons={coupons}
                            floatingFilter={floatingFilter}
                            selectedCategories={selectedCategories}
                            onCategoryChange={handleSingleCategoryChange}
                            searchValue={searchValue}
                            onSearchValueChange={setSearchValue}
                            onSearchChange={setSearchQuery}
                            setIsFilterModalOpen={openFilterModal}
                            hideFiltersLine={isCouponListScrolled || usedAdvancedFilters}
                            categories={activeCategories}
                            usedAdvancedFilters={usedAdvancedFilters}
                            selectedAdvancedFiltersCategories={selectedAdvancedFiltersCategories}
                            onAdvancedFiltersCategoriesChange={handleAdvancedFiltersCategoriesChange}
                            onClearAdvancedFilters={handleClearAdvancedFilters}
                            isShareButtonActive={isShareButtonActive}
                            setIsShareButtonActive={setIsShareButtonActive}
                            checkedCouponsCount={checkedCoupons.size}
                            onShareClick={() => setModals(prev => ({ ...prev, isShareModalOpen: true }))}
                            onCouponSelect={handleCouponSelectFromSearch}
                            onWhatsappClick={handleWhatsAppShare}
                            isViewingCoupon={!!selectedCouponId}
                            onOpenPublishModal={() => setModals(prev => ({ ...prev, isPublishModalOpen: true }))}
                            isPublishModalOpen={modals.isPublishModalOpen}
                        />
                    </SearchWrapper>
                    {isLoading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', height: '100%' }}>
                            <JustASquare />
                            <Text size='xl' weight='bold' textcolor='#888888' align='center'>טוען הטבות...</Text>
                        </div>
                    ) : coupons.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', height: '100%' }}>
                            <JustASquare />
                            <Text size='xl' weight='bold' textcolor='#888888' align='center'>אין הטבות זמינות כרגע,<br />ניתן לעבור לאיזור הראשי</Text>
                        </div>
                    ) : selectedCouponId && selectedCoupon ? (
                        <CouponSlug
                            coupon={selectedCoupon}
                            onBack={handleBackToList}
                            liked={isLiked(selectedCoupon.id)}
                            onToggleLiked={() => toggleLike(selectedCoupon.id)}
                            orgStore={appData.currentCommunityName}
                        />
                    ) : filteredCoupons.length === 0 && floatingFilter !== 'notifications' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', height: '100%' }}>
                            <JustASquare />
                            <Text size='xl' weight='bold' textcolor='#888888' align='center'>לא נמצאו הטבות</Text>
                        </div>
                    ) : floatingFilter === 'notifications' ? (
                        // On mobile, notifications are shown in the MainContent sidebar
                        // This div is just a placeholder to maintain layout
                        <div style={{ display: 'none' }} />
                    ) : floatingFilter === 'personal' ? (
                        <PersonalInformation
                            setIsConfirmModalOpen={(open) => {
                                setModals(prev => ({ ...prev, isConfirmModalOpen: open }))
                                if (open) setConfirmModalSource('blockMembers')
                            }}
                            setConfirmModalTitle={(title) => setConfirmModal(prev => ({ ...prev, title }))}
                            setConfirmModalSubtitle={(subtitle) => setConfirmModal(prev => ({ ...prev, subtitle }))}
                            setConfirmModalConfirmButtonText={(text) => setConfirmModal(prev => ({ ...prev, confirmButtonText: text }))}
                            setConfirmModalCancelButtonText={(text) => setConfirmModal(prev => ({ ...prev, cancelButtonText: text }))}
                            setConfirmModalIsConfettiVisible={(visible) => setConfirmModal(prev => ({ ...prev, isConfettiVisible: visible }))}
                            setConfirmModalIsGiftIconVisible={(visible) => setConfirmModal(prev => ({ ...prev, isGiftIconVisible: visible }))}
                            setConfirmModalConfirmButtonBackground={(bg) => setConfirmModal(prev => ({ ...prev, confirmButtonBackground: bg }))}
                            setConfirmModalConfirmButtonBorderColor={(color) => setConfirmModal(prev => ({ ...prev, confirmButtonBorderColor: color }))}
                            setConfirmModalConfirmButtonTextColor={(color) => setConfirmModal(prev => ({ ...prev, confirmButtonTextColor: color }))}
                            setConfirmModalCancelButtonBackground={(bg) => setConfirmModal(prev => ({ ...prev, cancelButtonBackground: bg }))}
                            setConfirmModalCancelButtonBorderColor={(color) => setConfirmModal(prev => ({ ...prev, cancelButtonBorderColor: color }))}
                            setConfirmModalCancelButtonTextColor={(color) => setConfirmModal(prev => ({ ...prev, cancelButtonTextColor: color }))}
                            setToastVisible={(visible) => setToast(prev => ({ ...prev, isVisible: visible }))}
                            setToastTitle={(title) => setToast(prev => ({ ...prev, title }))}
                            setToastSubtitle={(subtitle) => setToast(prev => ({ ...prev, subtitle }))}
                            setBlockedMembersCount={setBlockedMembersCount}
                            customerPreferences={customerPreferences}
                            saveCustomerPreferences={saveCustomerPreferences}
                            issuedCodes={issuedCodes}
                            onViewIssuedCode={(code) => {
                                setCurrentIssuedCode(code)
                                setModals(prev => ({ ...prev, isIssuedCouponViewOpen: true }))
                            }}
                        />
                    ) : (
                        <CouponCardsContainer ref={couponCardsRef}>
                            <div style={{ width: '100%', maxWidth: '900px' }}>
                                <AdBanner />
                            </div>
                            <CouponCardsGrid>
                                {withCardAds(filteredCoupons, cardAds).map(entry => 'ad' in entry ? (
                                    <AdCouponCard key={`ad-${entry.ad.id}`} ad={entry.ad} onClick={() => trackCardAdClick(entry.ad)} />
                                ) : (
                                    <CouponCard
                                        key={entry.coupon.id}
                                        image={entry.coupon.image}
                                        title={entry.coupon.title}
                                        subtitle={entry.coupon.subtitle}
                                        companyLogo={entry.coupon.companyLogo}
                                        discountPercentage={entry.coupon.discountPercentage}
                                        price={entry.coupon.price}
                                        hoursLeft={entry.coupon.hoursLeft}
                                        type={entry.coupon.type}
                                        liked={isLiked(entry.coupon.id)}
                                        onToggleLiked={() => toggleLike(entry.coupon.id)}
                                        isChecked={checkedCoupons.has(entry.coupon.id)}
                                        onCheckboxChange={(isChecked) => handleCheckboxChange(entry.coupon.id, isChecked)}
                                        onClick={() => handleCouponClick(entry.coupon.id)}
                                        endAt={entry.coupon.endAt}
                                    />
                                ))}
                            </CouponCardsGrid>
                        </CouponCardsContainer>
                    )}

                    {floatingFilter !== 'notifications' && (
                        <FloatingButtons>
                            {!selectedCouponId ? (
                                <>
                                    <FloatingButton
                                        color={floatingFilter === 'benefits' ? '#16AA40' : 'white'}
                                        onClick={() => setFloatingFilter('benefits')}
                                    >
                                        <CouponIcon fill={floatingFilter === 'benefits' ? 'white' : '#272727'} />
                                        <Text size='xl' weight='bold' textcolor={floatingFilter === 'benefits' ? 'white' : '#272727'}>הטבות</Text>
                                    </FloatingButton>
                                    <FloatingButton
                                        color={floatingFilter === 'liked' ? '#16AA40' : 'white'}
                                        onClick={() => setFloatingFilter('liked')}
                                    >
                                        <HeartIcon fill={floatingFilter === 'liked' ? 'white' : '#272727'} />
                                        <Text size='xl' weight='bold' textcolor={floatingFilter === 'liked' ? 'white' : '#272727'}>אהבתי</Text>
                                    </FloatingButton>
                                    <FloatingButton
                                        color={floatingFilter === 'personal' ? '#16AA40' : 'white'}
                                        onClick={() => setFloatingFilter('personal')}
                                    >
                                        <PersonIcon fill={floatingFilter === 'personal' ? 'white' : '#272727'} />
                                        <Text size='xl' weight='bold' textcolor={floatingFilter === 'personal' ? 'white' : '#272727'}>איזור אישי</Text>
                                    </FloatingButton>
                                </>
                            ) : (
                                <>
                                    <FloatingButton color='#007EFF' onClick={handleGetCoupon} disabled={isIssuingCode || isSelectedCouponExpired}>
                                        <Text size='xl' weight='bold' textcolor='white'>{isIssuingCode ? 'טוען...' : 'קבלת ההטבה'}</Text>
                                    </FloatingButton>
                                    <div
                                        style={{ display: 'flex', height: '62px', padding: '16px', justifyContent: 'center', alignItems: 'center', gap: '4px', flexShrink: '0', alignSelf: 'stretch', borderRadius: '8px', border: '1.5px solid #D1D1D1', cursor: 'pointer' }}
                                        onClick={() => {
                                            if (selectedCouponId) {
                                                setCheckedCoupons(new Set([selectedCouponId]))
                                                setModals(prev => ({ ...prev, isShareModalOpen: true }))
                                            }
                                        }}
                                    >
                                        <ShareIcon fill='#272727' />
                                        <Text size='xl' weight='bold' textcolor='primary'>שיתוף</Text>
                                    </div>

                                </>
                            )}
                        </FloatingButtons>
                    )}

                    <BottomNavMobile
                        floatingFilter={floatingFilter}
                        onFilterChange={setFloatingFilter}
                        isViewingCoupon={!!selectedCouponId && !modals.isGetCouponModalOpen}
                        isShareModeActive={isShareButtonActive}
                        isFilterModalOpen={modals.isFilterModalOpen}
                        isShareModalOpen={modals.isShareModalOpen}
                        checkedCouponsCount={checkedCoupons.size}
                        filteredCouponsCount={filteredCouponsCount}
                        onShareClick={() => {
                            if (selectedCouponId) {
                                setCheckedCoupons(new Set([selectedCouponId]))
                            }
                            setModals(prev => ({ ...prev, isShareModalOpen: true }))
                        }}
                        onAcceptClick={handleGetCoupon}
                        onCancelShareMode={() => {
                            setIsShareButtonActive(false)
                            setCheckedCoupons(new Set())
                        }}
                        onWhatsappClick={handleWhatsAppShare}
                        onApplyFilters={() => {
                            if (applyFiltersFromModal) {
                                applyFiltersFromModal()
                            }
                        }}
                        onCancelFilters={() => setModals(prev => ({ ...prev, isFilterModalOpen: false }))}
                        onConfirmShare={handleShare}
                        onCloseShareModal={() => setModals(prev => ({ ...prev, isShareModalOpen: false }))}
                        isConfirmShareDisabled={selectedContacts.size === 0}
                        isAcceptCouponModalOpen={modals.isGetCouponModalOpen || modals.isIssuedCouponViewOpen}
                        isCouponCodeCopied={modals.isIssuedCouponViewOpen ? true : isCodeCopied}
                        onContinuePurchase={handleOpenProductLink}
                        isPublishCouponModalOpen={modals.isPublishModalOpen}
                        publishBottomBarConfig={publishBottomBarConfig}
                        isAcceptDisabled={isSelectedCouponExpired}
                    />

                </ContentArea>
            </MainContent>
            {modals.isFilterModalOpen && (
                <>
                    <Backdrop onClick={() => setModals(prev => ({ ...prev, isFilterModalOpen: false }))} />
                    <FilterModal
                        onClose={() => setModals(prev => ({ ...prev, isFilterModalOpen: false }))}
                        categories={activeCategories}
                        validityFilters={validityFilters}
                        onValidityFilterChange={handleValidityFilterChange}
                        selectedAdvancedFiltersCategories={selectedAdvancedFiltersCategories}
                        onAdvancedFiltersCategoriesChange={handleAdvancedFiltersCategoriesChange}
                        onReset={handleReset}
                        coupons={filteredCoupons}
                        onApplyFilters={handleApplyFilters}
                        onFilteredCountChange={setFilteredCouponsCount}
                        onRegisterApplyCallback={handleRegisterApplyCallback}
                        currentSortOption={sortOption}
                        currentMinPrice={priceRange.min}
                        currentMaxPrice={priceRange.max}
                        sliderMax={sliderMax}
                        availableTags={availableTags}
                        selectedTags={selectedTags}
                        onTagsChange={setSelectedTags}
                    />
                </>
            )}
            {modals.isShareModalOpen && (
                <>
                    <Backdrop onClick={() => setModals(prev => ({ ...prev, isShareModalOpen: false }))} />
                    <ShareModal
                        onClose={() => setModals(prev => ({ ...prev, isShareModalOpen: false }))}
                        checkedCouponsCount={checkedCoupons.size}
                        onShare={handleShare}
                        selectedCouponIds={Array.from(checkedCoupons)}
                        selectedContacts={selectedContacts}
                        onContactSelect={handleContactSelect}
                        searchQuery={shareSearchQuery}
                        onSearchChange={setShareSearchQuery}
                        isSharing={isSharing}
                    />
                </>
            )}
            {modals.isGetCouponModalOpen && selectedCouponId && (
                <AcceptCouponModal
                    onClose={() => {
                        setModals(prev => ({ ...prev, isGetCouponModalOpen: false }))
                        setIsCodeCopied(false)
                        setCurrentIssuedCode(null)
                    }}
                    couponCode={currentIssuedCode?.code || coupons.find(coupon => coupon.id === selectedCouponId)?.couponCode || ''}
                    onContinue={handleOpenProductLink}
                    isCodeCopied={isCodeCopied}
                    onCodeCopied={() => setIsCodeCopied(true)}
                />
            )}
            {modals.isIssuedCouponViewOpen && currentIssuedCode && (
                <IssuedCouponView
                    issuedCode={currentIssuedCode}
                    onClose={() => {
                        setModals(prev => ({ ...prev, isIssuedCouponViewOpen: false }))
                        setCurrentIssuedCode(null)
                    }}
                    onContinue={handleOpenProductLink}
                />
            )}
            {modals.isConfirmModalOpen && (
                <>
                    <Backdrop onClick={() => setModals(prev => ({ ...prev, isConfirmModalOpen: false }))} />
                    <ConfirmModal
                        onClose={() => setModals(prev => ({ ...prev, isConfirmModalOpen: false }))}
                        onConfirm={getOnConfirmHandler()}
                        title={confirmModal.title}
                        subtitle={confirmModal.subtitle}
                        isConfettiVisible={confirmModal.isConfettiVisible}
                        isGiftIconVisible={confirmModal.isGiftIconVisible}
                        confirmButtonText={confirmModal.confirmButtonText}
                        cancelButtonText={confirmModal.cancelButtonText}
                        confirmButtonBackground={confirmModal.confirmButtonBackground}
                        confirmButtonBorderColor={confirmModal.confirmButtonBorderColor}
                        confirmButtonTextColor={confirmModal.confirmButtonTextColor}
                        cancelButtonBackground={confirmModal.cancelButtonBackground}
                        cancelButtonBorderColor={confirmModal.cancelButtonBorderColor}
                        cancelButtonTextColor={confirmModal.cancelButtonTextColor}
                    />
                </>
            )}

            {modals.isPublishModalOpen && (
                <PublishCouponModal
                    onClose={() => setModals(prev => ({ ...prev, isPublishModalOpen: false }))}
                    categories={localCategoriesForPublish}
                    onBottomBarConfig={setPublishBottomBarConfig}
                />
            )}

            <Toast isVisible={toast.isVisible} title={toast.title} subtitle={toast.subtitle} />

        </Container>
    )
}

export default Coupons